import { Check as CheckIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Avatar, Box, Card, CardContent, IconButton, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";

import { ConfirmDeleteDialog } from "~ui/molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";
import { MilestoneFormDialog } from "~ui/molecules/MilestoneFormDialog/MilestoneFormDialog";
import { TaskFormDialog } from "~ui/molecules/TaskFormDialog/TaskFormDialog";
import { makeProfileAvatarUrl } from "~utils/url";

// Centralized strings for internationalization
const tCardTitle = "📋 Tasks & Milestones";
const tAssignedTo = "Assigned to:";
const tDue = "Due:";
const tNoMilestones = "No milestones yet";
const tNoTasks = "No tasks yet";
const tTasks = (count: number) => `${count} tasks`;
const tMilestones = (count: number) => `${count} milestones`;
const tUnassigned = "Unassigned";

export interface Task {
  id: string;
  name: string;
  description: string;
  dueDate: string;
  userId?: string;
}

export interface Milestone {
  id: string;
  name: string;
  description: string;
  dueDate: string;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

export interface TasksMilestonesCardProps {
  tasks: Task[];
  milestones: Milestone[];
  canManage: boolean;
  onAddTask?: (task: { name: string; description: string; dueDate: Date; userId?: string }) => void;
  onDeleteTask?: (task: Task) => void;
  onCompleteTask?: (task: Task) => void;
  onAddMilestone?: (milestone: { name: string; description: string; dueDate: Date }) => void;
  onDeleteMilestone?: (milestone: Milestone) => void;
  onCompleteMilestone?: (milestone: Milestone) => void;
  members?: Member[];
}

export const TasksMilestonesCard = ({
  tasks,
  milestones,
  canManage,
  onAddTask,
  onDeleteTask,
  onCompleteTask,
  onAddMilestone,
  onDeleteMilestone,
  onCompleteMilestone,
  members = [],
}: TasksMilestonesCardProps) => {
  const [activeTab, setActiveTab] = useState<"tasks" | "milestones">("tasks");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: string;
    item: Task | Milestone;
  } | null>(null);

  const handleDeleteClick = (type: string, item: Task | Milestone) => {
    setItemToDelete({ item, type });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      if (itemToDelete.type === "task" && onDeleteTask) {
        onDeleteTask(itemToDelete.item as Task);
      } else if (itemToDelete.type === "milestone" && onDeleteMilestone) {
        onDeleteMilestone(itemToDelete.item as Milestone);
      }
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.25rem", fontWeight: "bold" }}>
            {tCardTitle}
          </Typography>
          {canManage && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {activeTab === "tasks" && onAddTask && (
                <Box sx={{ "& button": { minHeight: "32px", minWidth: "auto" } }}>
                  <TaskFormDialog
                    onAddTask={onAddTask}
                    members={members
                      .map((member) => ({
                        id: member.id,
                        name: `${member.firstName} ${member.lastName}`,
                      }))
                      .sort((a, b) => a.name.localeCompare(b.name))}
                  />
                </Box>
              )}
              {activeTab === "milestones" && onAddMilestone && (
                <Box sx={{ "& button": { minHeight: "32px", minWidth: "auto" } }}>
                  <MilestoneFormDialog onAddMilestone={onAddMilestone} />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            aria-label="Tasks and milestones tabs"
          >
            <Tab label={tTasks(tasks.length)} value="tasks" aria-label={`Tasks (${tasks.length})`} />
            <Tab
              label={tMilestones(milestones.length)}
              value="milestones"
              aria-label={`Milestones (${milestones.length})`}
            />
          </Tabs>
        </Box>

        <Box>
          {activeTab === "tasks" && (
            <Stack spacing={1}>
              {tasks.map((task, iterator) => (
                <Paper key={task.id} sx={{ p: 2 }}>
                  <Box
                    sx={{ alignItems: "flex-start", display: "flex", justifyContent: "space-between" }}
                    data-testid={`task-item-${iterator}`}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h3"
                        component="h3"
                        sx={{
                          fontSize: "1rem",
                          fontWeight: "bold",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {task.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {task.description}
                      </Typography>
                      {task.userId && (
                        <Box sx={{ alignItems: "center", display: "flex", mt: 0.5 }}>
                          <Typography variant="caption" sx={{ mr: 1 }}>
                            {tAssignedTo}
                          </Typography>
                          <Avatar
                            src={makeProfileAvatarUrl(task.userId)}
                            alt={members.find((m) => m.id === task.userId)?.firstName || ""}
                            sx={{ height: 20, mr: 0.5, width: 20 }}
                          >
                            {members.find((m) => m.id === task.userId)?.firstName?.[0] || ""}
                          </Avatar>
                          <Typography variant="caption">
                            {members.find((m) => m.id === task.userId)
                              ? `${members.find((m) => m.id === task.userId)?.firstName} ${members.find((m) => m.id === task.userId)?.lastName}`
                              : tUnassigned}
                          </Typography>
                        </Box>
                      )}
                      {task.dueDate && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          {tDue} {new Date(task.dueDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    {canManage && (onDeleteTask || onCompleteTask) && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {onDeleteTask && (
                          <IconButton
                            onClick={() => handleDeleteClick("task", task)}
                            size="small"
                            color="error"
                            aria-label={`Delete task: ${task.name}`}
                            data-testid={`task-delete-button-${iterator}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        {onCompleteTask && (
                          <IconButton
                            onClick={() => onCompleteTask(task)}
                            size="small"
                            color="success"
                            aria-label={`Complete task: ${task.name}`}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
              {tasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {tNoTasks}
                </Typography>
              )}
            </Stack>
          )}

          {activeTab === "milestones" && (
            <Stack spacing={1}>
              {milestones.map((milestone) => (
                <Paper key={milestone.id} sx={{ p: 2 }}>
                  <Box sx={{ alignItems: "flex-start", display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h3"
                        component="h3"
                        sx={{
                          fontSize: "1rem",
                          fontWeight: "bold",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {milestone.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {milestone.description}
                      </Typography>
                      {milestone.dueDate && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          {tDue} {new Date(milestone.dueDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    {canManage && (onDeleteMilestone || onCompleteMilestone) && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {onDeleteMilestone && (
                          <IconButton
                            onClick={() => handleDeleteClick("milestone", milestone)}
                            size="small"
                            color="error"
                            aria-label={`Delete milestone: ${milestone.name}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        {onCompleteMilestone && (
                          <IconButton
                            onClick={() => onCompleteMilestone(milestone)}
                            size="small"
                            color="success"
                            aria-label={`Complete milestone: ${milestone.name}`}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
              {milestones.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {tNoMilestones}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemType={itemToDelete?.type || "task"}
        itemName={itemToDelete?.item.name || ""}
        itemDescription={itemToDelete?.item.description}
      />
    </Card>
  );
};
